// Version builder card: which configured LLM provider this version runs on.
//
// This used to be a provider dropdown, a model dropdown and an API-key field —
// which meant pasting the same key into every agent you built. Now it selects
// from the tenant's provider connections, so the credential is configured once
// and this card cannot accept or display one at all.
//
// The select is grouped by provider and the resolved model is listed beneath
// it: provider is the choice, model is the consequence. They are not separate
// selects because they are not separate things — provider, model and key are
// one connection row, and splitting the control would imply you could mix them.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "@/components/ui/link";
import * as React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { withReturnTo } from "@/lib/return-to";
import { setVersionLlm } from "@/lib/api/endpoints";
import { useLlmModels, useVersionLlm } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { LlmModelView } from "@/types/api";
import { useIsOrgAdmin } from "@/lib/access";

/** The platform won't run this connection's model: discarded, or never offered. */
const unavailable = (m: LlmModelView) => m.discarded || m.in_catalog === false;

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function LlmConfigCard({ agentSlug, label }: { agentSlug: string; label: string }) {
  // Send the admin back to this version once they have added a model.
  const modelsHref = withReturnTo("/llm-models", `/agents/${agentSlug}/versions/${label}`);
  const qc = useQueryClient();
  const llm = useVersionLlm(agentSlug, label);
  const models = useLlmModels();
  // Providers (and their keys) are managed by org admins only.
  const canManage = useIsOrgAdmin();

  const [selected, setSelected] = React.useState<string>("");
  const loadedFor = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (llm.data && loadedFor.current !== label) {
      loadedFor.current = label;
      setSelected(llm.data.llm_model_slug ?? "");
    }
  }, [llm.data, label]);

  const save = useMutation({
    // "" is the inherit option, which the API expresses as null.
    mutationFn: () => setVersionLlm(agentSlug, label, selected || null),
    onSuccess: async () => {
      toast.success(selected ? "Model attached." : "Model detached.");
      await qc.invalidateQueries({ queryKey: qk.versionLlm(agentSlug, label) });
      await qc.invalidateQueries({ queryKey: qk.version(agentSlug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (llm.isLoading || models.isLoading) return <Skeleton className="h-44 w-full" />;
  if (llm.isError) return <ErrorState error={llm.error} onRetry={() => void llm.refetch()} />;

  const available = models.data ?? [];
  const selectedModel = available.find((m) => m.slug === selected);
  const dirty = (llm.data?.llm_model_slug ?? "") !== selected;

  return (
    <Card>
      <CardHeader>
        <CardTitle>LLM Provider</CardTitle>
        <CardDescription>
          Which configured provider connection this version runs on. The API key lives on the
          connection, so it is entered once and shared by every agent that selects it.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {available.length === 0 ? (
          <EmptyState
            title="No LLM providers configured"
            hint={
              canManage
                ? "Add one under LLM providers, then come back and select it here."
                : "Ask an org admin to add one under LLM providers, then select it here."
            }
            action={
              canManage ? (
                <Button asChild>
                  <Link href={modelsHref}>
                    Configure LLM providers <ExternalLink className="h-4 w-4" />
                  </Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="space-y-2">
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className={selectClass}
                aria-label="LLM provider"
              >
                <option value="">No provider — this version cannot run</option>
                {/* Grouped by provider: the provider is the choice being made,
                    the model underneath is the consequence of it. */}
                {Array.from(new Set(available.map((m) => m.provider)))
                  .sort()
                  .map((provider) => (
                    <optgroup key={provider} label={provider}>
                      {available
                        .filter((m) => m.provider === provider)
                        .map((m) => (
                          // A discarded model can't be attached (the API
                          // refuses it); kept visible so the current one reads
                          // correctly rather than vanishing from the select.
                          <option
                            key={m.slug}
                            value={m.slug}
                            disabled={unavailable(m) && m.slug !== selected}
                          >
                            {m.name}
                            {m.discarded
                              ? " (model discarded)"
                              : m.in_catalog === false
                                ? " (model not offered)"
                                : ""}
                          </option>
                        ))}
                    </optgroup>
                  ))}
              </select>
              {/* The model the chosen provider connection resolves to. Listed
                  rather than selected: provider and model travel together on
                  one connection, along with the key. */}
              {selectedModel ? (
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">{selectedModel.provider}</Badge>
                  <span className="font-mono text-muted-foreground">{selectedModel.model}</span>
                  {unavailable(selectedModel) ? (
                    <Badge variant="destructive">
                      {selectedModel.discarded ? "discarded" : "not offered"}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
              {selectedModel && unavailable(selectedModel) ? (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    The model <span className="font-mono">{selectedModel.model}</span>{" "}
                    {selectedModel.discarded
                      ? `has been discarded by the platform administrator${
                          selectedModel.discard_reason ? ` (${selectedModel.discard_reason})` : ""
                        }`
                      : "is not offered by this platform"}
                    . Runs of this version fail until you select a provider on a model that is
                    offered, or repoint this provider under LLM providers.
                  </div>
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {selected
                  ? "This version calls the model above with the connection's stored key."
                  : "Without a provider, runs of this version fail: there is no default key to fall back to."}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              {canManage ? (
                <Button variant="ghost" asChild>
                  <Link href={modelsHref}>Manage providers →</Link>
                </Button>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
